import ContactSalesModal from '@components/contactSalesModal/ContactSalesModal';
import ContactSupport from '@components/contactSupport/ContactSupport';
import UserMenu, { MenuKeys } from '@components/userMenu/UserMenu';
import cx from 'classnames';
import React, { Suspense, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { isNilOrEmpty } from '../../../helpers/extra';
import BrandIcon from '../../../images/chaos_icon.png';
import BackgroundSettings, {
  BACKGROUND_TYPES,
  BackgroundSetting,
  DEFAULT_BACKGROUND,
  backgroundTemplateMappings,
} from '../../backgroundSettings/BackgroundSettings';
import { useFirebaseProfileImage } from '../../backgroundSettings/useFirebaseBackgroundImage';
import MenuBar, { MenuOption } from '../../menuBar/MenuBar';

interface IUser {
  firstName?: string;
  lastName?: string;
  email: string;
  settings: {
    language?: string;
  };
}

interface AppLayoutProps {
  children: React.ReactNode;
  fillScreen?: boolean;
  selectedMenu?: MenuOption;
  user: IUser;
  onSelectedMenuChange: (value: MenuOption) => void;
  onLogoutClick: () => void;
  onLanguageChange: (value: string) => void;
  backgroundSetting: Record<string, string | null>;
  trialRemainingDays?: number;
  trialPeriod?: number;
  isTrialExpired?: boolean;
  userId?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  fillScreen = true,
  selectedMenu,
  user,
  onSelectedMenuChange,
  onLogoutClick,
  onLanguageChange,
  backgroundSetting,
  trialRemainingDays,
  trialPeriod,
  isTrialExpired,
  userId,
}) => {
  const navigate = useNavigate();

  const {
    bgImageFirebaseUrl,
    isLoading,
    uploadBgImageFirebase,
    removeBackgroundImageFirebase,
  } = useFirebaseProfileImage();

  const [openContactSupport, setOpenContactSupport] = useState<boolean>(false);
  const [openContactSales, setOpenContactSales] = useState<boolean>(false);
  const [openChangeBackgroundModal, setOpenChangeBackgroundModal] =
    useState<boolean>(false);

  const initialBackgroundSetting = isNilOrEmpty(backgroundSetting)
    ? DEFAULT_BACKGROUND
    : backgroundSetting;

  const [userBackgroundSetting, setUserBackgroundSetting] =
    useState<BackgroundSetting>(initialBackgroundSetting);

  const onSelectUserMenuItem = ({ key }: { key: string }) => {
    switch (key) {
      case MenuKeys.contactSales:
        setOpenContactSales(true);
        break;
      case MenuKeys.logout:
        onLogoutClick();
        break;
      case MenuKeys.settings:
        navigate('/settings');
        break;
      case MenuKeys.changeBackground:
        setOpenChangeBackgroundModal(true);
        break;
      case MenuKeys.contactSupport:
        setOpenContactSupport(true);
        break;
      case MenuKeys.en:
        onLanguageChange(key);
        break;
      case MenuKeys.fi:
        onLanguageChange(key);
        break;

      default:
    }
  };

  const dashboardBackground = useMemo(() => {
    if (
      userBackgroundSetting?.type === BACKGROUND_TYPES.photo &&
      bgImageFirebaseUrl
    ) {
      const backgroundImageUrl = userBackgroundSetting?.value
        ? userBackgroundSetting?.value
        : `url(${bgImageFirebaseUrl})`;
      return {
        backgroundImage:
          userBackgroundSetting?.value || bgImageFirebaseUrl
            ? backgroundImageUrl
            : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.4s ease-in-out',
        backgroundColor: '#FFF',
        opacity: bgImageFirebaseUrl && !isLoading ? 1 : 0,
      };
    }
    if (
      userBackgroundSetting?.type === BACKGROUND_TYPES.color &&
      userBackgroundSetting?.value
    ) {
      return { backgroundColor: userBackgroundSetting.value };
    }
    if (
      userBackgroundSetting?.type === BACKGROUND_TYPES.template &&
      userBackgroundSetting?.value &&
      backgroundTemplateMappings[userBackgroundSetting?.value]
    ) {
      return {
        backgroundImage: `url(${backgroundTemplateMappings[userBackgroundSetting?.value]}`,
        backgroundColor: 'var(--glass-background)',
      };
    }
    if (isLoading) {
      return {
        backgroundColor: 'var(--glass-background)',
      };
    }

    return {
      backgroundImage: `url(${backgroundTemplateMappings[DEFAULT_BACKGROUND.value]}`,
      backgroundColor: 'var(--glass-background)',
    };
  }, [
    bgImageFirebaseUrl,
    isLoading,
    userBackgroundSetting?.type,
    userBackgroundSetting.value,
  ]);

  return (
    <div
      className="tw-flex tw-min-h-screen tw-w-full tw-bg-center tw-bg-no-repeat tw-bg-cover tw-px-5 tw-py-6 tw-gap-4 tw-overflow-hidden"
      style={{
        ...dashboardBackground,
        transition:
          'background-color 0.5s ease-in-out, background 0.5s ease-in-out, opacity 0.5s ease-in-out',
      }}
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
        <img
          className="tw-w-[48px]"
          src={BrandIcon}
          alt="chaosarchitects-brand-icon"
        />
        <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center">
          <MenuBar selected={selectedMenu} onChange={onSelectedMenuChange} />
        </div>
        <div className="tw-mb-4">
          <UserMenu
            email={user.email}
            firstName={user.firstName}
            lastName={user.lastName}
            selectedLanguageCode={user?.settings?.language}
            onSelectMenuItem={onSelectUserMenuItem}
            trialDaysRemaining={trialRemainingDays}
            trialTotalDays={trialPeriod}
            isTrialExpired={isTrialExpired}
          />
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-w-full">
        <div
          className={cx('tw-flex-1', { 'tw-max-w-screen-2xl': !fillScreen })}
        >
          {children}
        </div>

        <ContactSupport
          openContactSupport={openContactSupport}
          setOpenContactSupport={setOpenContactSupport}
        />
        {isTrialExpired && openContactSales && (
          <Suspense fallback={null}>
            <ContactSalesModal
              isOpen
              onClose={() => setOpenContactSales(false)}
              userId={userId}
              source="user-menu"
            />
          </Suspense>
        )}
        <BackgroundSettings
          open={openChangeBackgroundModal}
          setOpen={setOpenChangeBackgroundModal}
          userBackgroundSetting={userBackgroundSetting}
          setUserBackgroundSetting={setUserBackgroundSetting}
          uploadBgImageFirebase={uploadBgImageFirebase}
          removeBackgroundImageFirebase={removeBackgroundImageFirebase}
          isImageUrlLoading={isLoading}
          userFirebaseImageUrl={bgImageFirebaseUrl}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  backgroundSetting: state?.user?.info?.settings?.backgroundSetting,
  trialRemainingDays: state?.user?.info?.hasLicense
    ? undefined
    : state?.user?.info?.trialRemainingDays,
  trialPeriod: state?.user?.info?.hasLicense
    ? undefined
    : state?.user?.info?.trialPeriod,
  isTrialExpired: Boolean(state?.user?.info?.isTrialExpired),
  userId: state?.user?.info?.userId,
});

export default connect(mapStateToProps)(AppLayout);
